// Phase-3 Slice 27 + 36 — step 2 of the rename plan: WHICH recorded references
// this rename touches, and with what confidence.
//
// Extracted from `renamePlanner.ts`, which stays the facade every caller
// imports. Everything here works over `ReferenceRecord[]`: the engine-aware
// identity context the matching is decided under, the per-kind candidate
// retrieval (`matchRelationRecords` / `filterToContainingTable`), and the two
// reference-level helpers the body-rewrite step consumes (`dedupeByRange`,
// `weakestConfidence`). Turning the surviving references into ALTER statements
// is `renameObjectBodyEdits.ts`.
//
// NAV-1 cross-table same-name disambiguation: the index keys column refs by
// bare name (`email`) on BOTH sides — the cursor hands the planner a
// schema/database-qualified column (`go-to/columnAtCursor.ts`) and
// `index/identifierIndex.ts`'s `identifierKey` drops that qualification for the
// column kind, without which the lookup returns nothing and Apply renames the
// column while leaving every dependent body stale. So `index.query` returns
// every `email` ref across all bodies. Each column record carries the
// `hostTable` it resolved to at index-build time (alias target /
// single-relation FROM scope), and `filterToContainingTable` drops any ref whose
// resolved host table differs from `request.containingTable`. Renaming
// `users.email` therefore never rewrites a body that references `orders.email`.
// Refs whose host couldn't be resolved at build time (no `hostTable`) are marked
// `'ambiguous'` and rewritten only under the preview's "include ambiguous
// matches" opt-in — see `filterToContainingTable` below.

import type { SqlEngine } from '../../sql-grammar/model';
import {
  catalogLeafRefMatches,
  effectiveDatabaseFor,
  matchRelationRef,
  mssqlCatalogCaseSensitiveFor,
  relationIdentitiesEqual,
  relationIdentityContextFor,
  type DatabaseComponent,
  type MatchConfidence,
  type RelationIdentityContext,
} from '../relationIdentity';
import type {
  IdentifierIndex,
  QualifiedName,
  ReferenceRecord,
  RenameRequest,
} from '../types';

import { getMysqlLowerCaseTableNamesFor } from '../mysqlCaseProbe';
import { mssqlDatabaseNamesCaseSensitive } from '../mssqlCollationProbe';

/** The caller's explicit case overrides — `PlanRenameInput`'s three optional
 *  fields, structurally. Each one absent (the normal case) falls back to what
 *  the probes / the waited-for index handle already know. */
export type RenameCaseOverrides = {
  readonly mysqlLowerCaseTableNames?: number;
  readonly mssqlCatalogCaseSensitive?: boolean;
  readonly mssqlDatabaseNamesCaseSensitive?: boolean;
};

export type CollectRenameReferencesInput = {
  readonly connectionId: string;
  readonly database: string;
  readonly request: RenameRequest;
  readonly sqlEngine: SqlEngine;
  /** The index for THIS database, already waited for by the planner. */
  readonly index: IdentifierIndex;
  readonly caseOverrides: RenameCaseOverrides;
  /** Per-ref match confidence, filled in by the matchers below. Relation refs
   *  get theirs from `matchRelationRef`; column refs get `'ambiguous'` when the
   *  index could not resolve the host relation they belong to
   *  (`filterToContainingTable`). Absent = `'exact'`. */
  readonly confidenceByRef: Map<ReferenceRecord, MatchConfidence>;
};

/** Every recorded reference that denotes the rename target, scored into
 *  `confidenceByRef`. Column renames go through the bare-key lookup plus the
 *  NAV-1 host-table filter; table/view renames through the broad relation
 *  candidate sweep plus the engine-aware comparator. */
export function collectRenameReferences(
  input: CollectRenameReferencesInput,
): readonly ReferenceRecord[] {
  const { connectionId, database, request, sqlEngine, index, confidenceByRef } = input;

  // #34: engine-aware relation-identity context. The effective target database
  // is the reference the rewrite resolves against: an explicit
  // fromIdentifier.database, else the connection's current database.
  //
  // Both case settings follow the same rule — the caller's explicit value when
  // it had one, else what the index the planner waited for knows:
  //   - MySQL: a cold request cannot know `@@lower_case_table_names` before that
  //     wait (`mysqlCaseProbe.ts`).
  //   - SQL Server: the per-database collation, read off the INDEX HANDLE
  //     rather than recomputed from `(connectionId, database)` — an empty
  //     `database` resolves to the connection's configured one and the
  //     collation is filed under THAT identity (see
  //     `mssqlCatalogCaseSensitiveFor`). In a `_CS_` database this keeps a
  //     rename of `dbo.V` from rewriting bodies that reference `dbo.v`.
  const identityCtx: RelationIdentityContext = relationIdentityContextFor(sqlEngine, {
    mysqlLowerCaseTableNames:
      input.caseOverrides.mysqlLowerCaseTableNames ??
      getMysqlLowerCaseTableNamesFor(connectionId, request.engineId),
    mssqlCatalogCaseSensitive:
      input.caseOverrides.mssqlCatalogCaseSensitive ??
      mssqlCatalogCaseSensitiveFor(index, sqlEngine),
    // The SERVER (instance) collation, NOT the database's — it decides whether
    // a stored reference to `sales.dbo.users` denotes the `Sales.dbo.users`
    // being renamed. One probe per connection answers it for every database
    // the connection spans, so it is read by connectionId rather than off the
    // per-database index handle.
    mssqlDatabaseNamesCaseSensitive:
      input.caseOverrides.mssqlDatabaseNamesCaseSensitive ??
      (sqlEngine === 'mssql' ? mssqlDatabaseNamesCaseSensitive(connectionId) : undefined),
  });

  if (request.kind === 'column') {
    // The column lookup's key is bare and lowercased on purpose (a discovery
    // index), so it also returns `T.id`'s references for a rename of `T.Id` on
    // a case-SENSITIVE SQL Server database, and `"Id"`'s for a rename of `id`
    // on PostgreSQL / Oracle — two different columns in each case.
    // `catalogLeafRefMatches` applies the same per-engine identity decision the
    // relation branch makes; it is a no-op on MySQL, SQLite and a `_CI_` SQL
    // Server database, whose lowercased key already IS their identity.
    return filterToContainingTable(
      index
        .query(request.fromIdentifier, 'column')
        .filter((r) =>
          catalogLeafRefMatches(r.identifier, request.fromIdentifier, 'column', identityCtx),
        ),
      request.containingTable!,
      identityCtx,
      confidenceByRef,
    );
  }

  const effectiveDb: DatabaseComponent | undefined = effectiveDatabaseFor(
    request.fromIdentifier,
    database || undefined,
    identityCtx,
  );
  return matchRelationRecords(
    index,
    request.fromIdentifier,
    effectiveDb,
    identityCtx,
    confidenceByRef,
  );
}

/** Collapse references whose byte ranges coincide OR overlap down to one edit
 *  per cluster. Two index records can describe the same reference with the same
 *  OR a different width:
 *
 *   - Identical range: the structural walker + the alias-qualified token scanner
 *     both emit a select-list `<alias>.<col>` at the same `(start,end)`.
 *   - Overlapping (nested) range: on MySQL the structural select-list walker
 *     trips on backticked aliases and captures the whole select item
 *     `` `u`.`email` AS `email` `` [24,46), while the alias-token fallback scanner
 *     captures just `` `u`.`email` `` [24,35). Both denote the SAME rename with
 *     the SAME replacement (`toIdentifier.name`), so keeping one is loss-free.
 *
 *  Distinct references can never overlap (separate tokens occupy disjoint byte
 *  spans), so an overlap always means one reference was double-recorded — making
 *  the collapse safe. We keep the NARROWEST overlapping range: it touches the
 *  fewest untouched bytes and, in the MySQL case, avoids clobbering the trailing
 *  `AS <alias>` (the view's output column name must not be renamed). Without this
 *  `emitSource` rejects the batch via its overlapping-edits guard and the whole
 *  preview throws (`EmitSourceError: planned edits overlap`).
 *
 *  When two duplicates share the SAME range the more-certain record wins — both
 *  describe one reference, and one scanner may have resolved its host relation
 *  where the other did not, so keeping the resolved one is strictly better than
 *  letting insertion order decide whether the edit lands behind the ambiguous
 *  opt-in. */
export function dedupeByRange(
  refs: readonly ReferenceRecord[],
  confidenceByRef: ReadonlyMap<ReferenceRecord, MatchConfidence>,
): readonly ReferenceRecord[] {
  // Sort by start asc, then end asc so the narrowest range at a shared start is
  // considered first and wins the greedy interval selection below; confidence
  // only breaks an exact tie.
  const certainty = (r: ReferenceRecord): number =>
    (confidenceByRef.get(r) ?? 'exact') === 'ambiguous' ? 1 : 0;
  const sorted = [...refs].sort(
    (a, b) =>
      a.range.startOffset - b.range.startOffset ||
      a.range.endOffset - b.range.endOffset ||
      certainty(a) - certainty(b),
  );
  const out: ReferenceRecord[] = [];
  let keptEnd = -1;
  for (const r of sorted) {
    // Adjacency (start === keptEnd) is allowed — only a true intersection
    // (start < keptEnd) is a duplicate-recording of an already-kept reference.
    if (r.range.startOffset < keptEnd) continue;
    out.push(r);
    keptEnd = r.range.endOffset;
  }
  return out;
}

/** NAV-1 cross-table same-name disambiguation. The column index keys refs by
 *  bare name (`email`), so `index.query` returns every `email` ref across all
 *  bodies regardless of which table it belongs to. Each column record now
 *  carries the `hostTable` it resolved to at index-build time; this filter
 *  drops any ref whose resolved host table is a *different* relation than the
 *  one the schema ALTER touches (`containingTable`). Renaming `users.email`
 *  therefore no longer rewrites a body that references `orders.email` (which
 *  would point at a now-nonexistent column).
 *
 *  A ref with NO recorded `hostTable` — the host could not be resolved at build
 *  time, e.g. a bare `GROUP BY email` over a multi-relation scope, or a column
 *  of a derived table / CTE alias — is neither dropped nor rewritten
 *  automatically. Accepting those unconditionally (the v1 rule) meant a rename
 *  of `users.email` rewrote a view over `orders` that merely grouped by a
 *  same-named column. They are recorded `'ambiguous'` instead, which routes them
 *  through the #34 machinery the relation branch already uses: counted in
 *  `ambiguousMatchCount`, excluded from `objectEdits` unless the user ticks the
 *  preview's "include ambiguous matches" box. A ref whose host IS resolved and
 *  differs is still dropped outright — that one is provably about another
 *  table, so no opt-in should resurrect it. */
function filterToContainingTable(
  records: readonly ReferenceRecord[],
  containingTable: QualifiedName,
  ctx: RelationIdentityContext,
  confidenceByRef: Map<ReferenceRecord, MatchConfidence>,
): readonly ReferenceRecord[] {
  return records.filter((r) => {
    if (!r.hostTable) {
      confidenceByRef.set(r, 'ambiguous');
      return true;
    }
    return relationIdentitiesEqual(r.hostTable, containingTable, ctx);
  });
}

/** #34: resolve which recorded relation references actually denote the rename
 *  target, and with what confidence. Candidate retrieval is deliberately broad
 *  (`queryRelationCandidates` keys on the leaf name only, so it collects every
 *  same-named relation across schemas / databases / case — and covers both the
 *  `'table'` and `'view'` buckets in one call). The engine-aware
 *  `matchRelationRef` comparator then makes the final per-component decision:
 *  it drops definite-different relations (differing explicit schema / db) and
 *  labels each surviving reference `exact | contextual | ambiguous`.
 *
 *  Replaces the old bare-name `mergeRelationRecords` (Plan 15 Gate C bridge
 *  fix): broad lookup keeps the gate-4b behaviour (schema-qualified request
 *  identifier still finds bare-recorded refs) while the comparator adds the
 *  schema-awareness #34 needs. The chosen confidence is recorded per reference
 *  in `confidenceByRef` so the planner can gate ambiguous rewrites. */
function matchRelationRecords(
  index: IdentifierIndex,
  target: QualifiedName,
  effectiveTargetDb: DatabaseComponent | undefined,
  ctx: RelationIdentityContext,
  confidenceByRef: Map<ReferenceRecord, MatchConfidence>,
): readonly ReferenceRecord[] {
  const out: ReferenceRecord[] = [];
  for (const ref of index.queryRelationCandidates(target)) {
    const match = matchRelationRef(ref.identifier, ref.refPartCount, target, effectiveTargetDb, ctx);
    if (!match) continue;
    confidenceByRef.set(ref, match.confidence);
    out.push(ref);
  }
  return out;
}

/** Weakest (most-uncertain) confidence among the references that make up a
 *  single body edit. Ordering exact > contextual > ambiguous; the edit is only
 *  as trustworthy as its least-certain reference. Missing entries default to
 *  `exact` — a relation ref the comparator scored, or a column ref whose host
 *  table the index resolved to the renamed table. Column renames populate
 *  `confidenceByRef` only for hostTable-less refs (`filterToContainingTable`). */
export function weakestConfidence(
  refs: readonly ReferenceRecord[],
  confidenceByRef: ReadonlyMap<ReferenceRecord, MatchConfidence>,
): MatchConfidence {
  let weakest: MatchConfidence = 'exact';
  for (const ref of refs) {
    const c = confidenceByRef.get(ref) ?? 'exact';
    if (c === 'ambiguous') return 'ambiguous';
    if (c === 'contextual') weakest = 'contextual';
  }
  return weakest;
}
